import type { Colors, Variants, Sizes } from '@/runtime/types';
import type { PropDoc } from '@/runtime/shared/elements-definition';
import { type HeadingProps, headingPropsDocs } from '../Heading';

/**
 * Card component props with type-safe color/variant combinations.
 *
 * @remarks
 * The `mono` color can only be used with the `solid` variant.
 * Using `mono` with other variants will cause a TypeScript error and a runtime warning.
 */
export type CardProps =
  | {
      /**
       * The element this component should render as.
       * @defaultValue `article`
       */
      as?: HTMLElement | string;
      /**
       * The card color (mono - only works with solid variant)
       * @defaultValue `neutral`
       */
      color: 'mono';
      /**
       * The card variant (must be solid when color is mono)
       * @defaultValue `subtle`
       */
      variant: 'solid';
      /**
       * The card size
       * @defaultValue `md`
       */
      size: Sizes;
    }
  | {
      /**
       * The element this component should render as.
       * @defaultValue `article`
       */
      as?: HTMLElement | string;
      /**
       * The card color
       * @defaultValue `neutral`
       */
      color: Exclude<Colors, 'mono'>;
      /**
       * The card variant
       * @defaultValue `subtle`
       */
      variant: Exclude<Variants, 'ghost' | 'link'>;
      /**
       * The card size
       * @defaultValue `md`
       */
      size: Sizes;
    };

/**
 * Internal normalized props interface.
 * Used for composables and internal logic to avoid discriminated union complexity.
 */
export interface NormalizedCardProps {
  color: Colors;
  variant: Exclude<Variants, 'ghost' | 'link'>;
  size: Sizes;
}

/**
 * Helper to normalize CardProps union to a simple interface.
 * Handles the mono+solid constraint enforcement.
 */
export function normalizeCardProps(props: CardProps): NormalizedCardProps {
  // TypeScript knows props is valid here due to the discriminated union
  // This function just helps with type narrowing for internal use
  return {
    color: props.color,
    variant: props.variant,
    size: props.size
  };
}

export interface CardHeaderProps extends HeadingProps {
  description?: string;
}

type CardPropKeys = keyof CardProps;

/**
 * API docs mapping for `<e-card>` props.
 *
 * Keys come from {@link CardProps}, values are used by the docs renderer
 * to build the props table.
 */
export const cardPropsDocs: Record<CardPropKeys, PropDoc> = {
  as: {
    name: 'as',
    type: 'string',
    descriptionKey: 'card.as.description'
  },
  color: {
    name: 'color',
    type: `'primary' | 'secondary' | 'accent' | 'error' | 'warning' | 'info' | 'success' | 'neutral' | 'mono'`,
    descriptionKey: 'card.color.description'
  },
  variant: {
    name: 'variant',
    type: `'solid' | 'outline' | 'soft' | 'subtle'`,
    descriptionKey: 'card.variant.description'
  },
  size: {
    name: 'size',
    type: `'xs' | 'sm' | 'md' | 'lg' | 'xl'`,
    descriptionKey: 'card.size.description'
  }
} as const;

type CardHeaderPropKeys = keyof CardHeaderProps;

/**
 * API docs mapping for `<e-card-header>` props.
 *
 * Keys come from {@link CardHeaderProps}, values are used by the docs renderer
 * to build the props table.
 */
export const cardHeaderPropsDocs: Record<CardHeaderPropKeys, PropDoc> = {
  ...headingPropsDocs,
  description: {
    name: 'description',
    type: 'string',
    descriptionKey: 'card.header.description.description'
  }
} as const;
